import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Spinner, Input, Button, Badge, Alert } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { handleApiResponse, createNotificationFromApiResponse, formatCreatedDate } from '../constants/utils';
import FileModal from '../components/Common/importExportModal';
import SparePartsModal from '../components/SparePartsModal';
import SparePartsForm from '../components/SparePartsForm';
import PermissionGuard from '../components/Common/PermissionGuard';
import usePermission from '../Hooks/usePermission';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';

const SpareParts = () => {
    const { t } = useTranslation();
    document.title = `${t('shared.spare_parts', 'Spare Parts')} | Arcmedix`;
    const { isAdmin } = usePermission();
    const [spareParts, setSpareParts] = useState([]);
    const [modal_large, setmodal_large] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [apiWarning, setApiWarning] = useState('');
    const [notification, setNotification] = useState({ type: '', message: '' });
    const [deleteModal, setDeleteModal] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [criticals, setCriticals] = useState([]);
    const [locations, setLocations] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [sparePartTypes, setSparePartTypes] = useState([]);
    const [assets, setAssets] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const currentUser = JSON.parse(localStorage.getItem('authUser'));
    const organizationId = currentUser?.activeOrganizationId ?? null;

    // Check if user can delete a spare part (must be owner or admin)
    const canDeleteSparePart = (part) => {
        if (isAdmin) return true;
        const createdById = part?.createdBy?.id ?? part?.createdBy;
        const userId = currentUser?.id;
        // If createdBy not present (backend not updated), allow based on permission only
        if (createdById === undefined || createdById === null) return true;
        return createdById === userId;
    };

    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fileModal, setFileModal] = useState(false);
    const [sparePartsModal, setSparePartsModal] = useState({
        isOpen: false,
        sparePartData: null,
        loading: false
    });

    // Filter state
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({
        vendorId: '',
        assetId: '',
        criticality: '',
        preferredStorageLocation: '',
        sparePartTypeId: '',
    });

    // Restock state
    const [restockModal, setRestockModal] = useState(false);
    const [restockData, setRestockData] = useState({ sparePartId: '', quantity: '', unitCost: '', notes: '' });
    const [restockErrors, setRestockErrors] = useState({});
    const [restockLoading, setRestockLoading] = useState(false);

    const buildFilterParams = (activeFilters = filters) => {
        const params = new URLSearchParams();
        if (activeFilters.vendorId) params.append('vendorId', activeFilters.vendorId);
        if (activeFilters.assetId) params.append('assetId', activeFilters.assetId);
        if (activeFilters.criticality) params.append('criticality', activeFilters.criticality);
        if (activeFilters.preferredStorageLocation) params.append('preferredStorageLocation', activeFilters.preferredStorageLocation);
        if (activeFilters.sparePartTypeId) params.append('sparePartTypeId', activeFilters.sparePartTypeId);
        const str = params.toString();
        return str ? `&${str}` : '';
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const fetchSpareParts = async (activeFilters = filters) => {
        try {
            setLoading(true);
            const filterParams = buildFilterParams(activeFilters);
            const { data } = await api.get(`spare-parts/all?organizationId=${organizationId || 1}&count=${count}${filterParams}`);
            setSpareParts(data?.response || []);
            setHasMore((data?.response?.length || 0) === count);
        } catch (error) {
            console.error('Error fetching spare parts:', error);
            setApiError(t('business.spare_parts.fetch_error', 'Failed to fetch spare parts data'));
            setSpareParts([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLocations = async () => {
        try {
            const { data } = await api.get(`organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`);
            setLocations(data.response[0]?.locations || []);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setLocations([]);
        }
    };

    const fetchAssetTypes = async () => {
        try {
            const { data } = await api.get(`organization-asset-type/getAll?organizationId=${organizationId}`);
            setAssetTypes(data.response || []);
        } catch (error) {
            console.error('Error fetching asset types:', error);
            setAssetTypes([]);
        }
    };

    const fetchSparePartTypes = async () => {
        try {
            const { data } = await api.get(`organization-spare-part-type/getAll?organizationId=${organizationId}`);
            setSparePartTypes(data.response || []);
        } catch (error) {
            console.error('Error fetching spare part types:', error);
            setSparePartTypes([]);
        }
    };

    const fetchAssets = async () => {
        try {
            const { data } = await api.get(`asset/all?organizationId=${organizationId}`);
            setAssets(data?.response || []);
        } catch (error) {
            console.error('Error fetching assets:', error);
            setAssets([]);
        }
    };

    const fetchManufacturers = async () => {
        try {
            const { data } = await api.get("asset/get-manufacturer");
            setManufacturers(data.response?.manufacturers || []);
        } catch (error) {
            console.error("Error fetching manufacturers:", error);
            setManufacturers([]);
        }
    };

    const fetchVendors = async () => {
        try {
            const { data } = await api.get(`vendor/getAllVendors`);
            setVendors(data?.response || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            setVendors([]);
        }
    };

    const fetchCriticality = async () => {
        try {
            const { data } = await api.get("dynamic-enum/find-by-name?name=criticality");
            setCriticals(data.response.values);
        } catch (error) {
            console.error("Error fetching criticality:", error);
            return null;
        }
    };

    const handleSearch = useCallback(async (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCount(10);

        try {
            setLoading(true);
            const searchParam = term.trim() ? `&search=${encodeURIComponent(term)}` : '';
            const filterParams = buildFilterParams();
            const { data } = await api.get(`spare-parts/all?organizationId=${organizationId || 1}&count=10${searchParam}${filterParams}`);
            setSpareParts(data?.response || []);
            setHasMore((data?.response?.length || 0) === 10);
        } catch (error) {
            console.error('Error searching spare parts:', error);
            setApiError(t('business.spare_parts.search_error', 'Failed to search spare parts'));
            setSpareParts([]);
        } finally {
            setLoading(false);
        }
    }, [organizationId, filters]);

    const handleLoadMore = async () => {
        if (!hasMore || loading) return;

        try {
            setLoading(true);
            const newCount = count + 10;
            setCount(newCount);
            const searchParam = searchTerm.trim() ? `&search=${encodeURIComponent(searchTerm)}` : '';
            const filterParams = buildFilterParams();
            const { data } = await api.get(`spare-parts/all?organizationId=${organizationId || 1}&count=${newCount}${searchParam}${filterParams}`);
            setSpareParts(data?.response || []);
            setHasMore((data?.response?.length || 0) === newCount);
        } catch (error) {
            console.error('Error loading more spare parts:', error);
            setApiError(t('business.spare_parts.load_more_error', 'Failed to load more spare parts'));
        } finally {
            setLoading(false);
        }
    };

    const handleSparePartClick = (part) => {
        setSparePartsModal({
            isOpen: true,
            sparePartData: part,
            loading: false
        });
    };

    const toggleSparePartsModal = () => {
        setSparePartsModal({
            isOpen: false,
            sparePartData: null,
            loading: false
        });
    };

    const handleAdd = () => {
        setSelectedEntry({
            name: '',
            sparePartTypeId: null,
            type: '',
            batchNumber: '',
            selectedAssets: [],
            vendorId: '',
            description: '',
            manufacturerName: '',
            manufacturerPartNumber: '',
            criticality: 'HIGH',
            quantity: '',
            minimumStockLevel: '',
            preferredStorageLocation: '',
            averageUnitCost: '',
            currency: null,
            imageUrls: [],
            documentUrls: [],
        });
        setErrors({});
        setApiError('');
        setApiWarning('');
        setmodal_large(true);
    };

    const handleEdit = (entry) => {
        // Handle asset as array (API now returns asset as array)
        const selectedAssets = entry.asset && Array.isArray(entry.asset) ? entry.asset : [];

        setSelectedEntry({
            ...entry,
            selectedAssets,
            vendorId: entry.vendor?.id || '',
            sparePartTypeId: entry.sparePartType?.id || null,
            name: entry.name || '',
            type: entry.type || '',
            batchNumber: entry.batchNumber || '',
            description: entry.description || '',
            manufacturerName: entry.manufacturerName || '',
            manufacturerPartNumber: entry.manufacturerPartNumber || '',
            criticality: entry.criticality || 'HIGH',
            minimumStockLevel: entry.minimumStockLevel?.toString() || '',
            preferredStorageLocation: entry.preferredStorageLocation || '',
            averageUnitCost: entry.averageUnitCost?.toString() || '',
            currency: entry.currency || null,
            imageUrls: entry.imageUrls || [],
            documentUrls: entry.documentUrls || [],
        });
        setErrors({});
        setApiError('');
        setApiWarning('');
        setmodal_large(true);
    };

    const handleDelete = async () => {
        if (!selectedEntry) return;

        try {
            await api.delete(`spare-parts/delete?id=${selectedEntry?.id}`);
            setNotification({ type: 'success', message: t('business.spare_parts.delete_success', 'Spare part deleted successfully') });
            fetchSpareParts();
            setDeleteModal(false);
            setSelectedEntry(null);
        } catch (error) {
            console.error('Error deleting spare part:', error);
            setNotification({ type: 'error', message: t('business.spare_parts.delete_error', 'Failed to delete spare part') });
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setErrors({});
        setApiError('');
        setApiWarning('');

        const data = {
            organizationId: organizationId || 1,
            name: selectedEntry?.name || '',
            sparePartTypeId: selectedEntry?.sparePartTypeId || null,
            type: selectedEntry?.type || '',
            batchNumber: selectedEntry?.batchNumber || '',
            assetId: selectedEntry?.selectedAssets?.map(asset => asset.id) || [], // Send all selected asset IDs
            vendorId: selectedEntry?.vendorId || null,
            description: selectedEntry?.description || '',
            manufacturerName: selectedEntry?.manufacturerName || '',
            manufacturerPartNumber: selectedEntry?.manufacturerPartNumber || '',
            criticality: selectedEntry?.criticality || 'HIGH',
            minimumStockLevel: selectedEntry?.minimumStockLevel ? Number(selectedEntry.minimumStockLevel) : 0,
            preferredStorageLocation: selectedEntry?.preferredStorageLocation || '',
            averageUnitCost: selectedEntry?.averageUnitCost ? Number(selectedEntry.averageUnitCost) : null,
            currency: selectedEntry?.currency || null,
            imageUrls: selectedEntry?.imageUrls || [],
            documentUrls: selectedEntry?.documentUrls || [],
        };

        if (selectedEntry?.id) {
            data.id = selectedEntry.id;
        } else {
            // Only send quantity on create — use Restock to adjust after creation
            data.quantity = selectedEntry?.quantity ? Number(selectedEntry.quantity) : 0;
        }

        const newErrors = {};
        if (!selectedEntry?.name) {
            newErrors.name = t('shared.name_is_required', 'Name is required');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            let response;
            if (selectedEntry?.id) {
                // Update - use PUT method
                response = await api.put('spare-parts/batch', [data]);
            } else {
                // Create - use POST method
                response = await api.post('spare-parts/batch', [data]);
            }

            const result = handleApiResponse(response.data);

            if (result.success && !result.hasWarnings) {
                setNotification(createNotificationFromApiResponse(result));
                setmodal_large(false);
                fetchSpareParts();
            } else if (result.success && result.hasWarnings) {
                // Show warnings in modal and keep it open
                const warningMessage = Array.isArray(result.warnings)
                    ? result.warnings.join(', ')
                    : result.warnings;
                setApiWarning(warningMessage);
                fetchSpareParts();
            }
        } catch (error) {
            console.error('Error saving spare part:', error);
            setApiError(error.response?.data?.message || t('business.spare_parts.save_error', 'Failed to save spare part'));
        }
    };

    const validateRestockForm = () => {
        const newErrors = {};
        if (!restockData.sparePartId) newErrors.sparePartId = t('business.spare_parts.select_spare_part_to_restock', 'Select spare part to restock');
        if (!restockData.quantity || Number(restockData.quantity) === 0) newErrors.quantity = t('business.spare_parts.quantity_cannot_be_zero', 'Quantity must not be zero');
        if (Number(restockData.quantity) < 0 && !restockData.notes?.trim()) newErrors.notes = t('business.spare_parts.notes_required_negative', 'Notes are required for negative quantity adjustments');
        setRestockErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRestockSubmit = async () => {
        if (!validateRestockForm()) return;
        setRestockLoading(true);
        try {
            const payload = {
                sparePartId: Number(restockData.sparePartId),
                quantity: Number(restockData.quantity),
                unitCost: restockData.unitCost ? Number(restockData.unitCost) : null,
                notes: restockData.notes || null
            };
            const response = await api.post('spare-parts/restock', payload);
            const result = handleApiResponse(response.data);
            if (result.success) {
                setNotification({ type: 'success', message: t('business.spare_parts.restock_success', 'Spare part restocked successfully') });
                setRestockModal(false);
                setRestockData({ sparePartId: '', quantity: '', unitCost: '', notes: '' });
                fetchSpareParts();
            } else {
                setNotification({ type: 'danger', message: result.message || t('business.spare_parts.restock_error', 'Failed to restock spare part') });
            }
        } catch (error) {
            console.error('Error restocking spare part:', error);
            setNotification({ type: 'danger', message: t('business.spare_parts.restock_error', 'Failed to restock spare part') });
        } finally {
            setRestockLoading(false);
        }
    };

    useEffect(() => {
        fetchSpareParts();
        fetchVendors();
        fetchCriticality();
        fetchLocations();
        fetchAssetTypes();
        fetchSparePartTypes();
        fetchAssets();
        fetchManufacturers();
    }, []);

    const handleImport = async (file) => {
        try {
            setLoading(true);
            setApiError('');

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const sparePartsToImport = jsonData
                .map(item => ({
                    organizationId: organizationId || 1,
                    assetId: item['Asset ID'] ? [Number(item['Asset ID'])] : [],
                    vendorId: item['Vendor ID'] || null,
                    name: item['Name']?.toString() || '',
                    type: item['Type']?.toString() || '',
                    batchNumber: item['Batch Number']?.toString() || '',
                    description: item['Description']?.toString() || '',
                    manufacturerName: item['Manufacturer Name']?.toString() || '',
                    manufacturerPartNumber: item['Manufacturer Part Number']?.toString() || '',
                    criticality: item['Criticality']?.toString() || 'HIGH',
                    minimumStockLevel: Number(item['Minimum Stock Level']) || 0,
                    preferredStorageLocation: item['Preferred Storage Location']?.toString() || ''
                }))
                .filter(item => item.name && item.assetId.length > 0);

            if (sparePartsToImport.length === 0) {
                setApiError(t('business.spare_parts.import_no_valid', 'No valid spare parts found in the uploaded file'));
                setLoading(false);
                return;
            }

            const response = await api.post('spare-parts/batch', sparePartsToImport);

            if (response.status === 200) {
                setNotification({
                    type: 'success',
                    message: t('business.spare_parts.import_success', 'Successfully imported {{count}} spare parts', { count: sparePartsToImport.length })
                });
                fetchSpareParts();
            }
        } catch (error) {
            console.error('Import error:', error);
            setApiError(error.response?.data?.message || t('business.spare_parts.import_error', 'Failed to import spare parts'));
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!spareParts || spareParts.length === 0) return;

        const dataToExport = spareParts.map((part, index) => ({
            '#': index + 1,
            'Name': part.name || '',
            'Type': part.type || '',
            'Batch Number': part.batchNumber || '',
            'Asset': part.asset && Array.isArray(part.asset) ? part.asset.map(a => a.name).join(', ') : '',
            'Asset ID': part.asset && Array.isArray(part.asset) ? part.asset.map(a => a.id).join(', ') : '',
            'Vendor': part.vendor?.name || '',
            'Vendor ID': part.vendor?.id || '',
            'Description': part.description || '',
            'Manufacturer Name': part.manufacturerName || '',
            'Manufacturer Part Number': part.manufacturerPartNumber || '',
            'Criticality': part.criticality || '',
            'Minimum Stock Level': part.minimumStockLevel || 0,
            'Preferred Storage Location': part.preferredStorageLocation || ''
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Spare Parts');

        XLSX.writeFile(wb, `spare-parts_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    useEffect(() => {
        if (notification.message) {
            const timer = setTimeout(() => {
                setNotification({ type: '', message: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    return (
        <div className='page-content mt-4'>
            {notification.message && (
                <Alert color={notification.type === 'success' ? 'success' : 'danger'} className="mb-3">
                    {notification.message}
                </Alert>
            )}

            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="spare-part:create">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={handleAdd}
                        >
                            <FeatherIcon icon="plus" size={18} className="me-1" />
                            {t('business.spare_parts.add_spare_part', 'Add Spare Part')}
                        </button>
                    </PermissionGuard>
                    <PermissionGuard permission="spare-part:update">
                        <button
                            type="button"
                            className="btn btn-soft-primary waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
                            onClick={() => setRestockModal(true)}
                        >
                            <FeatherIcon icon="plus-circle" />
                            {t('business.spare_parts.restock', 'Restock')}
                        </button>
                    </PermissionGuard>
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder={t('business.spare_parts.search_placeholder', 'Search Spare Parts...')}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t('shared.spare_parts', 'Spare Parts')}</h3>
                        <div className="d-flex align-items-center gap-2">
                    <button
                        className={`btn p-2 ${hasActiveFilters ? 'btn-soft-primary' : 'btn-soft-secondary'}`}
                        onClick={() => setFilterModal(true)}
                        title={t('shared.filter', 'Filter')}
                    >
                        <FeatherIcon icon="filter" size={18} />
                    </button>
                    <button
                        className="btn btn-soft-info p-2"
                        onClick={() => setFileModal(true)}
                        title={t('business.spare_parts.import_export', 'Import/Export spare parts')}
                        >
                        <FeatherIcon icon="file-text" size={18} />
                    </button>
                                            </div>
                                        </div>
                </CardHeader>

                <CardBody>
                    {apiError && (
                        <Alert color="danger" className="mb-3">
                            {apiError}
                        </Alert>
                    )}

                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                                <tr>
                                    <th style={{width: '50px'}}>#</th>
                                    <th>{t('shared.name', 'Name')}</th>
                                    <th>{t('shared.type', 'Type')}</th>
                                    <th>{t('shared.vendor', 'Vendor')}</th>
                                    <th>{t('business.spare_parts.in_stock_quantity', 'In Stock')}</th>
                                    <th>{t('shared.min_stock_level', 'Min Stock')}</th>
                                    <th>{t('shared.criticality', 'Criticality')}</th>
                                    <th style={{width: '120px'}}>{t('shared.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                {spareParts?.map((part, index) => (
                                    <motion.tr
                                        key={part?.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <th scope="row" style={{ verticalAlign: 'middle' }}>{index + 1}</th>
                                        <td
                                            className='text-primary'
                                            role='button'
                                            onClick={() => handleSparePartClick(part)}
                                            style={{ cursor: 'pointer', textDecoration: 'underline', verticalAlign: 'middle' }}
                                        >
                                            {part.name || t('shared.n_a', 'N/A')}
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>{part.sparePartType?.name || part.type || '-'}</td>
                                        <td style={{ verticalAlign: 'middle' }}>{part.vendor?.name || '-'}</td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <span className={part.quantity < part.minimumStockLevel ? 'text-danger fw-bold' : ''}>
                                                {part.quantity}
                                                {part.quantity < part.minimumStockLevel && <FeatherIcon icon="alert-triangle" size={14} className="ms-1" />}
                                            </span>
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>{part.minimumStockLevel || 0}</td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            {part.criticality ? (
                                                <Badge color={part.criticality === 'HIGH' ? 'danger' : part.criticality === 'MEDIUM' ? 'warning' : 'success'}>
                                                    {t(`shared.${part.criticality.toLowerCase()}`, part.criticality)}
                                                </Badge>
                                            ) : '-'}
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <div className="d-flex gap-3">
                                                <PermissionGuard permission="spare-part:update">
                                                    <span className='text-warning' role='button' onClick={() => handleEdit(part)}>
                                                        <FeatherIcon icon="edit" />
                                                    </span>
                                                </PermissionGuard>
                                                <PermissionGuard permission="spare-part:delete">
                                                    {canDeleteSparePart(part) && (
                                                        <span className='text-danger' role='button' onClick={() => { setSelectedEntry(part); setDeleteModal(true); }}>
                                                            <FeatherIcon icon="trash-2" />
                                                        </span>
                                                    )}
                                                </PermissionGuard>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                        {loading && (
                            <div className="text-center py-3">
                                <Spinner color="primary" />
                            </div>
                        )}
                    </div>
                    {spareParts.length === 0 && !loading && (
                        <div className="text-center py-4">
                            <p className="text-muted">{t('business.spare_parts.no_spare_parts', 'No spare parts found')}</p>
                        </div>
                    )}
                    {hasMore && spareParts.length > 0 && !loading && (
                        <div className="text-center py-3">
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleLoadMore}
                                disabled={loading}
                            >
                                {t('shared.load', 'Load More')}
                            </button>
                        </div>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={modal_large} toggle={() => setmodal_large(false)} size="lg">
                <div className="modal-header">
                    <h5 className="modal-title">
                        {selectedEntry?.id ? t('shared.button_edit_spare_part', 'Edit Spare Part') : t('business.spare_parts.add_spare_part', 'Add Spare Part')}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setmodal_large(false)}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>
                <form onSubmit={handleSave}>
                    <div className="modal-body">
                        {errors.general && (
                            <Alert color="danger">{errors.general}</Alert>
                        )}

                        <SparePartsForm
                            formData={selectedEntry}
                            onChange={(updates) => setSelectedEntry(prev => ({...prev, ...updates}))}
                            errors={errors}
                            apiError={apiError}
                            apiWarning={apiWarning}
                            vendors={vendors}
                            enumData={{criticality: criticals}}
                            organizationId={organizationId}
                            manufacturers={manufacturers}
                            locations={locations}
                            assetTypes={assetTypes}
                            sparePartTypes={sparePartTypes}
                            isEditMode={!!selectedEntry?.id}
                        />
                    </div>
                    <div className="modal-footer">
                        <Button type="button" color="secondary" onClick={() => setmodal_large(false)}>
                            {t('shared.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" color="primary">
                            {selectedEntry?.id ? t('shared.update', 'Update') : t('shared.save', 'Save')}
                        </Button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title">{t('shared.confirm_delete', 'Confirm Delete')}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setDeleteModal(false)}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>
                <div className="modal-body">
                    <p>{t('business.spare_parts.confirm_delete_message', 'Are you sure you want to delete this spare part? This action cannot be undone.')}</p>
                </div>
                <div className="modal-footer">
                    <Button type="button" color="secondary" onClick={() => setDeleteModal(false)}>
                        {t('shared.cancel', 'Cancel')}
                    </Button>
                    <Button type="button" color="danger" onClick={handleDelete}>
                        {t('shared.delete', 'Delete')}
                    </Button>
                </div>
            </Modal>

            <FileModal
                title='Spare Parts'
                isOpen={fileModal}
                toggle={() => setFileModal(false)}
                data={spareParts}
                onImport={handleImport}
                onExport={handleExport}
                error={apiError}
                loading={loading}
                importLabel="Import Spare Parts from Excel"
                exportLabel="Export Spare Parts to Excel"
            />

            {/* Restock Modal */}
            <Modal isOpen={restockModal} toggle={() => { setRestockModal(false); setRestockErrors({}); }} size="md">
                <div className="modal-header">
                    <h5 className="modal-title">{t('business.spare_parts.restock_spare_part', 'Restock Spare Part')}</h5>
                    <button type="button" className="btn-close" onClick={() => { setRestockModal(false); setRestockErrors({}); }} />
                </div>
                <div className="modal-body">
                    <div className="mb-4">
                        <label className="form-label fw-medium">{t('business.spare_parts.spare_part_singular', 'Spare Part')} <span className="text-danger">*</span></label>
                        <Select
                            options={spareParts.map(sp => ({ value: sp.id, label: sp.name }))}
                            value={restockData.sparePartId ? spareParts.filter(sp => sp.id === Number(restockData.sparePartId)).map(sp => ({ value: sp.id, label: sp.name }))[0] || null : null}
                            onChange={(selected) => setRestockData(prev => ({...prev, sparePartId: selected?.value || ''}))}
                            isClearable
                            isSearchable
                            placeholder={t('business.spare_parts.select_spare_part', 'Select Spare Part')}
                            classNamePrefix="react-select"
                        />
                        {restockErrors.sparePartId && <small className="text-danger">{restockErrors.sparePartId}</small>}
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-medium">{t('business.spare_parts.quantity', 'Quantity')} <span className="text-danger">*</span></label>
                        <Input type="number" className={restockErrors.quantity ? 'is-invalid' : ''} value={restockData.quantity} onChange={(e) => setRestockData(prev => ({...prev, quantity: e.target.value}))} placeholder={t('business.spare_parts.enter_quantity', 'Enter quantity')} />
                        <small className="text-muted">{t('shared.use_negative_for_adjustment', 'Use negative values for stock adjustments')}</small>
                        {restockErrors.quantity && <small className="text-danger d-block">{restockErrors.quantity}</small>}
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-medium">{t('business.spare_parts.unit_cost', 'Unit Cost')}</label>
                        <Input type="number" min="0" step="0.01" value={restockData.unitCost} onChange={(e) => setRestockData(prev => ({...prev, unitCost: e.target.value}))} placeholder={t('business.spare_parts.enter_unit_cost', 'Enter unit cost')} />
                    </div>
                    <div className="mb-4">
                        <label className="form-label fw-medium">{t('business.spare_parts.notes', 'Notes')}</label>
                        <Input type="textarea" rows={3} className={restockErrors.notes ? 'is-invalid' : ''} value={restockData.notes} onChange={(e) => setRestockData(prev => ({...prev, notes: e.target.value}))} placeholder={t('business.spare_parts.enter_notes', 'Enter notes for this adjustment')} />
                        {restockErrors.notes && <small className="text-danger">{restockErrors.notes}</small>}
                    </div>
                </div>
                <div className="modal-footer">
                    <Button color="secondary" onClick={() => { setRestockModal(false); setRestockErrors({}); }}>{t('shared.cancel', 'Cancel')}</Button>
                    <Button color="primary" onClick={handleRestockSubmit} disabled={restockLoading}>
                        {restockLoading ? <><Spinner size="sm" className="me-2" />{t('shared.submitting', 'Submitting...')}</> : t('business.spare_parts.restock', 'Restock')}
                    </Button>
                </div>
            </Modal>

            {/* Filter Modal */}
            <Modal
                isOpen={filterModal}
                toggle={() => setFilterModal(false)}
                size="lg"
                className="fade-scale-modal"
                backdropClassName="modal-backdrop-blur"
                centered
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header text-white">
                        <h5 className="modal-title fs-4">{t('business.spare_parts.filter_spare_parts', 'Filter Spare Parts')}</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setFilterModal(false)}
                            aria-label={t('shared.close', 'Close')}
                        ></button>
                    </div>

                    <div className="modal-body p-4">
                        <Row className="mb-4 g-3">
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterVendor" className="form-label fw-medium">
                                        {t('shared.vendor', 'Vendor')}
                                    </label>
                                    <select
                                        id="filterVendor"
                                        className="form-select"
                                        value={filters.vendorId}
                                        onChange={(e) => setFilters({ ...filters, vendorId: e.target.value })}
                                    >
                                        <option value="">{t('shared.all_vendors', 'All Vendors')}</option>
                                        {vendors?.map((vendor) => (
                                            <option key={vendor.id} value={vendor.id}>
                                                {vendor.vendorName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterSparePartType" className="form-label fw-medium">
                                        {t('shared.type', 'Type')}
                                    </label>
                                    <select
                                        id="filterSparePartType"
                                        className="form-select"
                                        value={filters.sparePartTypeId}
                                        onChange={(e) => setFilters({ ...filters, sparePartTypeId: e.target.value })}
                                    >
                                        <option value="">{t('shared.all_types', 'All Types')}</option>
                                        {sparePartTypes?.map((spt) => (
                                            <option key={spt.id} value={spt.id}>
                                                {spt.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterCriticality" className="form-label fw-medium">
                                        {t('shared.criticality', 'Criticality')}
                                    </label>
                                    <select
                                        id="filterCriticality"
                                        className="form-select"
                                        value={filters.criticality}
                                        onChange={(e) => setFilters({ ...filters, criticality: e.target.value })}
                                    >
                                        <option value="">{t('shared.all_criticalities', 'All Criticalities')}</option>
                                        {criticals?.map((c) => (
                                            <option key={c} value={c}>
                                                {t(`shared.${c.toLowerCase()}`, c)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterLocation" className="form-label fw-medium">
                                        {t('business.spare_parts.preferred_storage_location', 'Storage Location')}
                                    </label>
                                    <select
                                        id="filterLocation"
                                        className="form-select"
                                        value={filters.preferredStorageLocation}
                                        onChange={(e) => setFilters({ ...filters, preferredStorageLocation: e.target.value })}
                                    >
                                        <option value="">{t('shared.all_locations', 'All Locations')}</option>
                                        {locations?.map((location) => (
                                            <option key={location.id} value={location.locationName}>
                                                {location.locationName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterAsset" className="form-label fw-medium">
                                        {t('shared.asset', 'Asset')}
                                    </label>
                                    <select
                                        id="filterAsset"
                                        className="form-select"
                                        value={filters.assetId}
                                        onChange={(e) => setFilters({ ...filters, assetId: e.target.value })}
                                    >
                                        <option value="">{t('shared.all_assets', 'All Assets')}</option>
                                        {assets?.map((asset) => (
                                            <option key={asset.id} value={asset.id}>
                                                {asset.assetName || asset.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="modal-footer bg-light border-top-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                const clearedFilters = {
                                    vendorId: '',
                                    assetId: '',
                                    criticality: '',
                                    preferredStorageLocation: '',
                                    sparePartTypeId: '',
                                };
                                setFilters(clearedFilters);
                                setFilterModal(false);
                                fetchSpareParts(clearedFilters);
                            }}
                        >
                            {t('shared.clear_all', 'Clear All')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                setFilterModal(false);
                                fetchSpareParts(filters);
                            }}
                        >
                            {t('shared.apply_filters', 'Apply Filters')}
                        </button>
                    </div>
                </motion.div>
            </Modal>

            {/* Spare Parts Modal */}
            <SparePartsModal
                isOpen={sparePartsModal.isOpen}
                toggle={toggleSparePartsModal}
                sparePartData={sparePartsModal.sparePartData}
                onEdit={handleEdit}
                onRestock={(part) => {
                    setRestockData(prev => ({...prev, sparePartId: part.id?.toString() || ''}));
                    setRestockModal(true);
                }}
            />

        </div>
    );
};

export default SpareParts;
